<template>
  <div class="info-table reservation" v-if="event">
    <v-icon>mdi-map-marker</v-icon>
    <div class="d-flex">
      <span :class="{'max-70': showChip}">
        {{ event.resource && event.resource.title }}
        <template v-if="resourceSiteTitle">
          | {{ resourceSiteTitle }}
        </template>
      </span>
      <v-spacer/>
      <v-chip
          v-if="showChip"
          :color="eventColor(event)">
        <template v-if="event.rejected">
          Rejected
        </template>
        <template v-else-if="!event.approved">
          Needs Approval
        </template>
        <template v-else-if="event.active">
          Checked In
        </template>
        <template v-else-if="event.checkedOut">
          Checked Out
        </template>
      </v-chip>
    </div>

    <v-tooltip v-if="!event.rejected && !event.approved" bottom>
      <template #activator="{on}">
        <div class="r">
          <v-btn
              v-on="on"
              :color="eventColor(event)"
              @click="event.approval = true"
              class="white--text">
            <v-icon left small>mdi-check</v-icon>
            approve
          </v-btn>
        </div>
      </template>
      Approve this reservation
    </v-tooltip>

    <v-icon>mdi-account-check</v-icon>
    <v-checkbox
        label="Check-in required"
        hide-details
        class="mt-0 pt-0"
        v-model="event.requiresCheckIn"/>

    <v-expand-transition>
      <v-icon v-if="showPrepTimes">mdi-clock</v-icon>
    </v-expand-transition>
    <v-expand-transition>
      <div v-if="showPrepTimes" class="setup-times">
        <v-text-field
            label="Setup"
            suffix="mins"
            outlined
            hide-details
            placeholder="0"
            dense
            v-model.number="event.setupMins"
            type="number"
            step="5"
            min="0"/>
        <v-text-field
            label="Clean down"
            suffix="mins"
            outlined
            hide-details
            placeholder="0"
            dense
            v-model.number="event.cleanDownMins"
            type="number"
            step="5"
            min="0"/>
        <v-tooltip bottom>
          <template #activator="{on}">
            <v-btn icon @click="removeSetupTimes" v-on="on">
              <v-icon>mdi-close-circle-outline</v-icon>
            </v-btn>
          </template>
          Remove setup and clean down time
        </v-tooltip>
      </div>
    </v-expand-transition>

    <v-expand-transition>
      <v-icon v-if="showNote">mdi-text-box</v-icon>
    </v-expand-transition>
    <v-expand-transition>
      <v-textarea
          v-if="showNote"
          ref="noteField"
          v-model="event.note"
          placeholder="Remember to setup the whiteboard"
          hide-details
          filled
          class="notes-field"
          auto-grow
          rows="4"
          background-color="amber lighten-4"/>
    </v-expand-transition>

    <v-expand-transition>
      <v-icon v-if="showCatering">mdi-coffee</v-icon>
    </v-expand-transition>
    <v-expand-transition>
      <catering-description
          v-if="showCatering"
          :catering-request.sync="internalCateringRequest"
          :catering-is-enabled="cateringIsEnabled"
          :catering-can-be-edited="cateringCanBeEdited"/>
    </v-expand-transition>

    <span v-if="!readOnly" class="r">
      <a v-if="!showPrepTimes" @click="addSetupTime" class="mr-4">Add setup time...</a>
      <a v-if="!showNote" @click="showNoteOverride = true; focusNoteField()" class="mr-4">Add a note...</a>
      <a
          v-if="!showCatering && cateringIsEnabled && cateringCanBeEdited"
          @click="showCateringOverride = true;">
        Add catering...
      </a>
    </span>
    <v-divider class="mb-2 r"/>
  </div>
</template>

<script>
import {mapGetters, mapMutations, mapState} from 'vuex';
import {seconds} from '@/util/dates';
import eventColor from '@/views/room-booking/calendar/event-color';
import {compareEditDuration} from '@/util/times';
import CateringDescription from '@/views/room-booking/calendar/components/CateringDescription';

export default {
  name: 'Reservation',
  components: {CateringDescription},
  mixins: [eventColor],
  props: {
    path: {
      type: String,
      default: null
    },
    id: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      showNoteOverride: null,
      showSetupTimeOverride: null,
      showCateringOverride: null,
      focusNoteFieldStart: null, // for tracking how long we've been attempting to focus the note field
      maxFocusNoteFieldWait: 2 * seconds
    };
  },
  computed: {
    ...mapState('time', ['now']),
    ...mapGetters('views/roomBooking/booking', ['eventByKey']),
    ...mapGetters('sites', ['sitesById']),
    showChip() {
      return !this.event.approved || this.event.active || this.event.checkedOut;
    },
    event() {
      return this.eventByKey({ref: this.path, id: this.id});
    },
    cateringIsEnabled() {
      return this.event.cateringIsEnabled;
    },
    cateringCanBeEdited() {
      return compareEditDuration(this.event.cateringEditTimeEnd, this.event.start, this.now);
    },
    readOnly() {
      return Boolean(this.event.blocked || this.event.privacy || this.event.checkedOut);
    },
    connectionsIn() {
      return this.event.connectionsIn || [];
    },
    hasConnectionsIn() {
      return this.connectionsIn.length > 0;
    },
    hasAdjacentTimes() {
      return this.event.hasAdjacentBefore || this.event.hasAdjacentAfter;
    },
    showPrepTimes() {
      if (this.showSetupTimeOverride !== null) return this.showSetupTimeOverride;
      return this.hasConnectionsIn && this.hasAdjacentTimes;
    },
    showNote() {
      return this.showNoteOverride && !this.readOnly || Boolean(this.event.note);
    },
    showCatering() {
      return Boolean(this.showCateringOverride && !this.readOnly || this.event.firstCateringWorkOrder);
    },
    workOrderRefs() {
      return this.event.workOrderRefs;
    },

    resourceSiteTitle() {
      const resource = this.event.resource;
      if (!resource) {
        return null;
      }
      const ancestors = resource._groups_ancestors || [];
      const ancestorIds = ancestors.map(a => a.id);
      for (const id of ancestorIds) {
        if (this.sitesById.hasOwnProperty(id)) {
          return this.sitesById[id].title;
        }
      }
      return null;
    },
    internalCateringRequest: {
      get() {
        return this.event.firstCateringWorkOrder;
      },
      set(v) {
        if (this.showCateringOverride && !v.value) {
          // no catering request to remove, this is entirely a local change
          this.showCateringOverride = false;
        }
        this.event.firstCateringWorkOrder = v;
      }
    }
  },
  watch: {
    event() {
      this.showSetupTimeOverride = null;
    },
    hasAdjacentTimes: {
      immediate: true,
      handler(b) {
        // if the event already has setup time, force show it
        if (b) {
          this.showSetupTimeOverride = true;
        }
      }
    },
    workOrderRefs: {
      immediate: true,
      handler(v) {
        this.setWorkOrderRefs(v || []);
      }
    }
  },
  methods: {
    ...mapMutations('views/roomBooking/booking/workOrders', ['setWorkOrderRefs']),
    addSetupTime() {
      this.event.setupMins = 15;
      this.event.cleanDownMins = 15;
    },
    removeSetupTimes() {
      this.event.setupMins = 0;
      this.event.cleanDownMins = 0;
      this.showSetupTimeOverride = null;
    },

    focusNoteField() {
      if (!this.$refs.noteField) {
        if (this.focusNoteFieldStart !== null) {
          // not the first time we've tried, check if we should give up
          if (Date.now() - this.focusNoteFieldStart > this.maxFocusNoteFieldWait) {
            this.focusNoteFieldStart = null;
            return; // give up
          }
        } else {
          this.focusNoteFieldStart = Date.now();
        }
        setTimeout(() => {
          this.focusNoteField();
        }, 10);
      } else {
        this.focusNoteFieldStart = null;
        this.$refs.noteField.focus();
      }
    }
  }
};
</script>

<style scoped>
.reservation.info-table {
  grid-gap: 16px;
  align-items: start;
}

.reservation.info-table .l {
  grid-column-start: 1;
}

.reservation.info-table .r {
  grid-column-start: 2;
}

.setup-times {
  font-size: 16px;
  display: grid;
  grid-template-columns: repeat(2, 8em) auto;
  grid-gap: 8px;
}

.notes-field >>> textarea {
  overflow: auto;
  max-height: 15em;
  margin-top: 0 !important;
  padding-top: 10px;
  padding-bottom: 10px;
}

.max-70 {
  max-width: 70%;
}
</style>
